/**
 * WithdrawCard — форма вывода с 2FA-подтверждением.
 *
 * Flow:
 *   1. Юзер заполняет форму (сумма, сеть, адрес) → клик "Вывести"
 *   2. Backend создаёт вывод, списывает с баланса, шлёт 6-значный код
 *      в Telegram юзеру (через @moonraid_game_bot)
 *   3. UI переключается на экран "Введите код"
 *   4. Юзер вводит код → status = pending, вывод передаётся админу
 *   5. Админ исполняет, юзер получает уведомление "Вывод исполнен"
 *
 * Все тексты на "Вы".
 */
import { useEffect, useRef, useState } from 'react';
import {
  Loader2,
  Shield,
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  RefreshCw,
  Lock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GlassSurface, GlassButton } from '@/components/glass';
import { lunaApi } from '@/services/api';
import { useUISounds } from '@/hooks/useUISounds';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';

type NetworkBep = 'BEP20' | 'TON';
type Step = 'form' | 'verify' | 'success' | 'disabled';

interface Props {
  onAfterSuccess?: () => void;
}

export function WithdrawCard({ onAfterSuccess }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const uiSounds = useUISounds();
  const { haptic } = useTelegramWebApp();
  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [network, setNetwork] = useState<NetworkBep>('TON');

  // Verify
  const [withdrawalId, setWithdrawalId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [now, setNow] = useState<number>(Date.now());

  // Limit display
  const [limitInfo, setLimitInfo] = useState<{
    availableToday: string;
    dailyLimit: string;
    minWithdraw?: string;
    commissionPct?: string;
  } | null>(null);

  // Tick для обратного отсчёта TTL кода
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (step !== 'verify') return;
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [step]);

  // Limit on mount. Если платёжка в test-режиме — бэк возвращает
  // disabled=true, сразу переключаемся в step='disabled' чтобы юзер
  // не заполнял форму впустую.
  useEffect(() => {
    lunaApi.withdraw
      .limit()
      .then((r) => {
        const data = r.data as {
          availableToday: string;
          dailyLimit: string;
          minWithdraw?: string;
          commissionPct?: string;
          disabled?: boolean;
        };
        if (data.disabled) {
          setStep('disabled');
          return;
        }
        setLimitInfo(data);
      })
      .catch(() => {
        // silently — может быть test-режим где вывод отключён
      });
  }, []);

  async function onSubmitForm() {
    setError(null);
    if (!amount || Number(amount) <= 0) { setError(t('withdraw.amountError')); return; }
    if (!address || address.length < 10) { setError(t('withdraw.addressError')); return; }
    setLoading(true);
    try {
      const r = await lunaApi.withdraw.request(amount.trim(), address.trim(), network, 'telegram');
      setWithdrawalId(r.data.id);
      setExpiresAt(r.data.verificationExpiresAt ? new Date(r.data.verificationExpiresAt) : null);
      setStep('verify');
      setCode('');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string; message?: string; code?: string } } };
      const code = e.response?.data?.code;
      if (code === 'WITHDRAW_DISABLED_IN_TEST') {
        setStep('disabled');
        return;
      }
      setError(e.response?.data?.message || e.response?.data?.error || t('withdraw.requestError'));
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitCode() {
    setError(null);
    if (!/^\d{6}$/.test(code)) { setError(t('withdraw.codeLen')); return; }
    if (!withdrawalId) { setError(t('withdraw.noIdError')); return; }
    setLoading(true);
    try {
      await lunaApi.withdraw.verify(withdrawalId, code);
      setStep('success');
      onAfterSuccess?.();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string; error?: string } } };
      setError(e.response?.data?.message || e.response?.data?.error || t('withdraw.invalidCode'));
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    if (!withdrawalId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await lunaApi.withdraw.resend(withdrawalId, 'telegram');
      setExpiresAt(new Date(r.data.verificationExpiresAt));
      setCode('');
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || t('withdraw.resendError'));
    } finally {
      setLoading(false);
    }
  }

  function onStartOver() {
    setStep('form');
    setWithdrawalId(null);
    setCode('');
    setExpiresAt(null);
    setError(null);
  }

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------

  if (step === 'disabled') {
    return (
      <GlassSurface className="p-5">
        <div className="flex items-start gap-3">
          <Lock className="mt-0.5 h-5 w-5 text-amber-400" />
          <div>
            <h3 className="text-base font-medium text-gray-100">{t('withdraw.disabledTitle')}</h3>
            <p className="mt-1 text-xs text-gray-300">
              {t('withdraw.disabledHint')}
            </p>
          </div>
        </div>
      </GlassSurface>
    );
  }

  if (step === 'success') {
    return (
      <GlassSurface className="p-5">
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-400" />
          <h3 className="text-base font-medium text-gray-100">{t('withdraw.successTitle')}</h3>
          <p className="text-xs text-gray-300 max-w-sm">
            {t('withdraw.successHint')}
          </p>
        </div>
      </GlassSurface>
    );
  }

  if (step === 'verify') {
    const secondsLeft = expiresAt ? Math.max(0, Math.floor((expiresAt.getTime() - now) / 1000)) : 0;
    const mm = Math.floor(secondsLeft / 60);
    const ss = secondsLeft % 60;
    const expired = secondsLeft <= 0;
    return (
      <GlassSurface className="p-5">
        <button
          onClick={onStartOver}
          className="mb-3 inline-flex items-center gap-1 text-xs text-gray-300 hover:text-gray-300"
        >
          <ArrowLeft className="h-3 w-3" /> {t('withdraw.backToForm')}
        </button>
        <div className="mb-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-purple-400" />
          <h3 className="text-base font-medium text-gray-100">{t('withdraw.verifyTitle')}</h3>
        </div>
        <p className="mb-4 text-xs text-gray-300">
          {t('withdraw.verifyHint')}
        </p>

        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          autoFocus
          className="mb-2 w-full rounded-xl px-4 py-3 text-center font-mono text-2xl tracking-[0.5em] text-gray-100 focus:ring-2 focus:ring-purple-500"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        />

        <div className="mb-4 flex items-center justify-between text-[11px]">
          <span className={expired ? 'text-red-400' : 'text-gray-300'}>
            {expired ? t('withdraw.codeExpired') : t('withdraw.codeValid', { time: `${mm}:${String(ss).padStart(2, '0')}` })}
          </span>
          <button
            onClick={() => { uiSounds.paymentTap(); haptic.light(); onResend(); }}
            disabled={loading}
            className="inline-flex items-center gap-1 text-purple-300 hover:text-purple-200 disabled:opacity-40"
          >
            <RefreshCw className="h-3 w-3" />
            {t('withdraw.sendNew')}
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-900/50 bg-red-950/30 p-3 text-xs text-red-300">
            <AlertCircle className="mb-0.5 mr-1 inline h-3.5 w-3.5" /> {error}
          </div>
        )}

        <GlassButton
          onClick={() => { uiSounds.paymentTap(); haptic.medium(); onSubmitCode(); }}
          disabled={loading || code.length !== 6 || expired}
          className="w-full"
        >
          {loading ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
          {t('withdraw.confirm')}
        </GlassButton>
      </GlassSurface>
    );
  }

  // step === 'form'
  return (
    <GlassSurface className="p-5">
      <h3 className="mb-1 text-base font-medium text-gray-100">{t('withdraw.title')}</h3>
      <p className="mb-4 text-xs text-gray-300">
        {t('withdraw.hint')}
      </p>

      {limitInfo && (
        <div className="mb-4 rounded-xl p-3 text-xs"
          style={{ background: 'rgba(139, 92, 246, 0.06)', border: '1px solid rgba(139, 92, 246, 0.15)' }}
        >
          <div className="flex items-center justify-between text-gray-200">
            <span>{t('withdraw.availableToday')}</span>
            <span className="font-mono text-purple-300">
              {formatUsdt(limitInfo.availableToday, locale)} USDT
            </span>
          </div>
          <div className="mt-0.5 flex items-center justify-between text-gray-300">
            <span>{t('withdraw.dailyLimit')}</span>
            <span className="font-mono">{formatUsdt(limitInfo.dailyLimit, locale)} USDT</span>
          </div>
        </div>
      )}

      <div className="mb-3">
        <label className="mb-1.5 block text-[11px] uppercase tracking-wider text-gray-300">
          {t('withdraw.networkLabel')}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {(['TON', 'BEP20'] as const).map((n) => (
            <button
              key={n}
              onClick={() => { uiSounds.paymentTap(); haptic.light(); setNetwork(n); }}
              className={`rounded-xl px-3 py-2 text-xs font-medium ${
                network === n ? 'text-purple-200' : 'text-gray-300'
              }`}
              style={{
                background: network === n ? 'rgba(139, 92, 246, 0.12)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${network === n ? 'rgba(139, 92, 246, 0.35)' : 'rgba(255,255,255,0.06)'}`,
              }}
            >
              {n === 'BEP20' ? 'USDT BEP-20' : 'USDT TON'}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="mb-1.5 flex items-center justify-between text-[11px] uppercase tracking-wider text-gray-300">
          <span>{t('withdraw.amountLabel')}</span>
          {limitInfo?.minWithdraw && (
            <span className="normal-case tracking-normal text-[10.5px] text-gray-200">
              мин. {formatUsdt(limitInfo.minWithdraw, locale)} USDT
            </span>
          )}
        </label>
        <input
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
          placeholder={limitInfo?.minWithdraw ? formatUsdt(limitInfo.minWithdraw, locale) : '100'}
          className="w-full rounded-xl px-4 py-2.5 text-sm text-gray-100 focus:ring-2 focus:ring-purple-500"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        />
      </div>

      <div className="mb-4">
        <label className="mb-1.5 block text-[11px] uppercase tracking-wider text-gray-300">
          {t('withdraw.addressLabel')}
        </label>
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={network === 'BEP20' ? '0x...' : 'UQ...'}
          className="w-full rounded-xl px-4 py-2.5 text-sm text-gray-100 font-mono focus:ring-2 focus:ring-purple-500"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        />
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-900/50 bg-red-950/30 p-3 text-xs text-red-300">
          <AlertCircle className="mb-0.5 mr-1 inline h-3.5 w-3.5" /> {error}
        </div>
      )}

      <GlassButton onClick={() => { uiSounds.paymentTap(); haptic.medium(); onSubmitForm(); }} disabled={loading} className="w-full">
        {loading ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
        {t('withdraw.continueBtn')}
      </GlassButton>

      <p className="mt-3 text-[10px] text-gray-200">
        {t('withdraw.feeNote')}
      </p>
    </GlassSurface>
  );
}

function formatUsdt(v: string | number, locale: string = 'ru-RU'): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!isFinite(n)) return '0';
  // Без хвостовых нулей: 1 → "1", 1.5 → "1,5", 1.234 → "1,23".
  if (Number.isInteger(n)) return n.toLocaleString(locale);
  return n.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
